"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Tooltip,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Header from "../components/header";
import GoogleMap from "../components/googlemap";
import { useNavigate, useSearchParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import getApiBase from "../components/getApiBase";
import { resolveVisitSiteHref } from "../utils/visitSiteHref";
import { listShowsHours, pinHoursText } from "../utils/savedShopHours";
import featuredBadge from "../assets/feature-badges/badge-design-3-shield.png";
import cornerBadgeRight from "../assets/feature-badges/badge-design-5-corner-trimmed-right.png";

const DEFAULT_CENTER = { lat: 28.5383, lng: -81.3792 };

function hoursLine(shop, hoursToday) {
  if (!listShowsHours(shop, hoursToday)) return null;
  const label = pinHoursText(shop, hoursToday);
  return label ? `🕒 ${label}` : "🕓 Hours unavailable";
}

function getDistanceInMiles(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Main React component that wraps and renders the Google Map and shop list UI.
 *
 * Handles:
 * - User geolocation and ZIP detection
 * - Fetching and sorting auto repair shops
 * - Responsive layout rendering (map vs list)
 */

function GoogleWrapper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [zip, setZip] = useState("");
  const [nodes, setNodes] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(12);
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [userMovedMap, setUserMovedMap] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [userRole, setUserRole] = useState(false);
  const [radius, setRadius] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openStatusMap, setOpenStatusMap] = useState({});
  const [hoursToday, setHoursToday] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");

  useEffect(() => {
    if (["Mechanic", "Owner"].includes(storedUser?.role)) {
      setUserRole(true);
    }
  }, [storedUser]);

  const fetchShops = async (zipToUse = zip) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/autoshops?zip=${zipToUse}&radius=${radius}`
      );
      const data = await res.json();
      setNodes(data);
      const first = data.find((shop) => shop.lat && shop.lng);
      if (mapInitialized && first) {
        setMapCenter({ lat: first.lat, lng: first.lng });
      }
    } catch (err) {
      console.error("❌ Failed to fetch autoshops:", err);
      setNodes([]);
    }
  };

  // Check for zip and radius query parameters from URL (must be after fetchShops is defined)
  useEffect(() => {
    const zipFromUrl = searchParams.get("zip");
    const radiusFromUrl = searchParams.get("radius");

    if (zipFromUrl) {
      console.log(`📍 ZIP code from URL: ${zipFromUrl}`);
      setZip(zipFromUrl);
      setLoadingLocation(false); // Don't wait for geolocation

      // Use radius from URL if provided, otherwise keep default
      if (radiusFromUrl) {
        const radiusNum = parseFloat(radiusFromUrl);
        if (!isNaN(radiusNum) && radiusNum > 0) {
          setRadius(radiusNum);
          console.log(`📏 Radius from URL: ${radiusNum} miles`);
        }
      }

      fetchShops(zipFromUrl);
    }
  }, [searchParams]);

  const handleVisitShop = async (shop) => {
    if (storedUser && !userRole) {
      try {
        await fetch(`${getApiBase()}/api/visitedShops`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            autoshop_id: shop.id,
            user_id: storedUser.id,
          }),
        });
      } catch (err) {
        console.error("Error saving visited shop:", err);
      }
    }
  };

  useEffect(() => {
    // Skip geolocation if ZIP was provided via URL query parameter
    const zipFromUrl = searchParams.get("zip");
    if (zipFromUrl) {
      return; // Already handled in the previous useEffect
    }

    const lookupZipFromLatLng = async (lat, lng) => {
      try {
        const response = await fetch(
          `${getApiBase()}/api/autoshops/geocode?lat=${lat}&lng=${lng}`
        );
        const data = await response.json();
        let zipCode = null;
        for (const result of data.results) {
          for (const component of result.address_components) {
            if (component.types.includes("postal_code")) {
              zipCode = component.long_name;
              break;
            }
          }
          if (zipCode) break;
        }
        if (zipCode) {
          setZip(zipCode);
          await fetchShops(zipCode);
        }
      } catch (err) {
        console.error("Geocoding failed:", err);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          if (!mapInitialized) setMapCenter({ lat, lng });
          setUserLocation({ lat, lng });
          await lookupZipFromLatLng(lat, lng);
          setLoadingLocation(false);
        },
        () => setLoadingLocation(false),
        { timeout: 10000 }
      );
    } else {
      setLoadingLocation(false);
    }
  }, [searchParams]);

  const fetchOpenNowStatus = async (placeId) => {
    if (!placeId || openStatusMap[placeId] !== undefined) return; // skip if already fetched

    try {
      const res = await fetch(
        `${getApiBase()}/api/autoshops/open-now?placeId=${placeId}`,
      );
      const data = await res.json();
      setOpenStatusMap((prev) => ({ ...prev, [placeId]: data.openNow }));
    } catch (err) {
      console.error("Error fetching openNow:", err);
      setOpenStatusMap((prev) => ({ ...prev, [placeId]: null }));
    }
  };

  const fetchHours = async (placeId) => {
    if (!placeId || hoursToday[placeId] !== undefined) return;

    try {
      const res = await fetch(
        `${getApiBase()}/api/autoshops/businesshours?place_search=${placeId}`
      );
      const data = await res.json();

      setHoursToday((prev) => ({ ...prev, [placeId]: data.todayHours || null }));
    } catch (err) {
      console.error("Error fetching hours:", err);
      setHoursToday(prev => ({
        ...prev,
        [placeId]: null,
      }));
    }
  };


  const handleRefresh = async () => {
    if (!zip) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiBase()}/api/autoshops/refresh?zip=${zip}&radius=${radius}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      const data = await response.json();
      setNodes(data);
      const first = data.find((shop) => shop.lat && shop.lng);
      if (mapInitialized && first) {
        setMapCenter({ lat: first.lat, lng: first.lng });
      }
    } catch (error) {
      console.error("🔁 Refresh failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const first = nodes.find((shop) => shop.lat && shop.lng);
    if (mapInitialized && first) {
      setMapCenter({ lat: first.lat, lng: first.lng });
    }
  }, [nodes]);

  useEffect(() => {
    if (selectedShop?.place_id) {
      fetchHours(selectedShop.place_id);
    }
  }, [selectedShop]);


  // Use backend Priority Listing sorting order (no client-side re-sorting)
  // Backend already applies subscription-based sorting with fair rotation
  const sortedNodes = [...nodes];

  if (loadingLocation) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Getting your location…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ zIndex: 3, backgroundColor: "white" }}>
        <Header />
      </Box>

      {isMobile ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: showResults ? "40vh" : "90vh",
              minHeight: "250px",
            }}
          >
            <GoogleMap
              nodes={nodes}
              mapCenter={mapCenter}
              zoom={zoom}
              selected={selectedShop}
              onSelect={setSelectedShop}
              onCameraChange={({ center, zoom }) => {
                setMapCenter(center);
                setZoom(zoom);
                setUserMovedMap(true);
              }}
              userLocation={userLocation}
            />
          </Box>

          <Button
            variant="contained"
            onClick={() => setShowResults(!showResults)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 10,
              backgroundColor: "#1976d2",
            }}
          >
            {showResults ? "Hide" : "Show"} Results
          </Button>

          {showResults && (
            <Box
              sx={{
                width: "100vw",
                flex: 1,
                overflowY: "auto",
                pt: 2,
                pb: 4,
                px: 2,
                backgroundColor: "#fff",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Find Autoshops Near You
              </Typography>
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                  label="ZIP Code"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  select
                  label="Search Radius"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value={5}>Nearby (5 mi)</MenuItem>
                  <MenuItem value={10}>Local (10 mi)</MenuItem>
                  <MenuItem value={15}>Regional (15 mi)</MenuItem>
                  <MenuItem value={25}>Extended (25 mi)</MenuItem>
                  <MenuItem value={50}>Max (50 mi)</MenuItem>
                </TextField>

                <Button
                  variant="contained"
                  onClick={() => {
                    setMapInitialized(true);
                    fetchShops(zip);
                  }}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Search
                </Button>
                <Tooltip title="Refresh Autoshops from Server">
                  <span>
                    <Button
                      variant="outlined"
                      onClick={handleRefresh}
                      disabled={loading || !zip}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                    ></Button>
                  </span>
                </Tooltip>
              </Box>

              {sortedNodes.map((shop, index) => {
                const distance =
                  userLocation && shop.lat && shop.lng
                    ? getDistanceInMiles(
                      userLocation.lat,
                      userLocation.lng,
                      shop.lat,
                      shop.lng
                    ).toFixed(2)
                    : null;

                return (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      mx: "auto",
                      width: "100%",
                      maxWidth: "600px",
                      px: 1,
                      cursor: "pointer",
                      border: selectedShop?.id === shop.id ? "3px solid #FFD700" : "1px solid #ddd",
                      backgroundColor: selectedShop?.id === shop.id ? "#FFF9E6" : "white",
                      position: "relative",
                      overflow: "visible",
                    }}
                    onClick={() => {
                      setSelectedShop(shop);
                    }}
                    onMouseEnter={() => {
                      fetchOpenNowStatus(shop.place_id);
                      fetchHours(shop.place_id);
                    }}
                  >
                    {shop.has_featured_badge && (
                      <Box
                        component="img"
                        src={cornerBadgeRight}
                        alt="Featured Shop Corner Right"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: "60px",
                          height: "60px",
                          zIndex: 10,
                        }}
                      />
                    )}
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {shop.shop_name}
                        {shop.in_network && (
                          <Tooltip title="Verified In-Network Shop">
                            <CheckCircleIcon fontSize="small" color="success" />
                          </Tooltip>
                        )}
                      </Typography>
                      <Typography>{shop.address}</Typography>
                      <Typography>Rating: ⭐ {shop.rating}</Typography>
                      {openStatusMap[shop.place_id] !== undefined && (
                        <Typography>
                          {openStatusMap[shop.place_id] === true
                            ? "✅ Open Now"
                            : openStatusMap[shop.place_id] === false
                              ? "❌ Closed"
                              : "🕓 Hours Unavailable"}
                        </Typography>
                      )}

                      {hoursLine(shop, hoursToday) && (
                        <Typography sx={{ mt: 0.5 }}>
                          {hoursLine(shop, hoursToday)}
                        </Typography>
                      )}

                      {distance && (
                        <Typography sx={{ mt: 1 }}>
                          📍 {distance} miles away
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <Button
                          href={resolveVisitSiteHref(shop)}
                          target="_blank"
                          variant="outlined"
                          onClick={() => handleVisitShop(shop)}
                          disabled={!resolveVisitSiteHref(shop)}
                        >
                          Visit Site
                        </Button>
                        {shop.lat && shop.lng && (
                          <Button
                            href={`https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lng}`}
                            target="_blank"
                            variant="contained"
                          >
                            Get Directions
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flex: 1 }}>
          <Box
            sx={{
              width: "400px",
              height: "100vh",
              overflowY: "auto",
              backgroundColor: "white",
              zIndex: 2,
              p: 3,
            }}
          >
            <Typography variant="h4" gutterBottom>
              Find Autoshops Near You
            </Typography>
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <TextField
                label="ZIP Code"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                sx={{ minWidth: 120 }}
              />
              <Tooltip title="Refresh Autoshops from Server">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={loading || !zip}
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <RefreshIcon />
                    }
                  ></Button>
                </span>
              </Tooltip>
              <TextField
                select
                label="Search Radius"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value={5}>Nearby (5 mi)</MenuItem>
                <MenuItem value={10}>Local (10 mi)</MenuItem>
                <MenuItem value={15}>Regional (15 mi)</MenuItem>
                <MenuItem value={25}>Extended (25 mi)</MenuItem>
                <MenuItem value={50}>Max (50 mi)</MenuItem>
              </TextField>

              <Button
                variant="contained"
                onClick={() => {
                  setMapInitialized(true);
                  fetchShops(zip);
                }}
                sx={{ whiteSpace: "nowrap" }}
              >
                Search
              </Button>
            </Box>

            {sortedNodes.map((shop, index) => {
              const distance =
                userLocation && shop.lat && shop.lng
                  ? getDistanceInMiles(
                    userLocation.lat,
                    userLocation.lng,
                    shop.lat,
                    shop.lng
                  ).toFixed(2)
                  : null;

              return (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    cursor: "pointer",
                    border: selectedShop?.id === shop.id ? "3px solid #FFD700" : "1px solid #ddd",
                    backgroundColor: selectedShop?.id === shop.id ? "#FFF9E6" : "white",
                    position: "relative",
                    overflow: "visible",
                  }}
                  onClick={() => {
                    setSelectedShop(shop);
                  }}
                  onMouseEnter={() => {
                    fetchOpenNowStatus(shop.place_id);
                    fetchHours(shop.place_id);

                  }}
                >
                  {shop.has_featured_badge && (
                    <Box
                      component="img"
                      src={cornerBadgeRight}
                      alt="Featured Shop Corner Right"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "60px",
                        height: "60px",
                        zIndex: 10,
                      }}
                    />
                  )}
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {shop.shop_name}
                      {shop.in_network && (
                        <Tooltip title="Verified In-Network Shop">
                          <CheckCircleIcon fontSize="small" color="success" />
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography>{shop.address}</Typography>
                    <Typography>Rating: ⭐ {shop.rating}</Typography>
                    {openStatusMap[shop.place_id] !== undefined && (
                      <Typography>
                        {openStatusMap[shop.place_id] === true
                          ? "✅ Open Now"
                          : openStatusMap[shop.place_id] === false
                            ? "❌ Closed"
                            : "🕓 Hours Unavailable"}
                      </Typography>
                    )}

                    {hoursLine(shop, hoursToday) && (
                      <Typography sx={{ mt: 0.5 }}>
                        {hoursLine(shop, hoursToday)}
                      </Typography>
                    )}

                    {distance && (
                      <Typography sx={{ mt: 1 }}>
                        📍 {distance} miles away
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Button
                        href={resolveVisitSiteHref(shop)}
                        target="_blank"
                        variant="outlined"
                        onClick={() => handleVisitShop(shop)}
                        disabled={!resolveVisitSiteHref(shop)}
                      >
                        Visit Site
                      </Button>
                      {shop.lat && shop.lng && (
                        <Button
                          href={`https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lng}`}
                          target="_blank"
                          variant="contained"
                        >
                          Get Directions
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, height: "100vh", position: "sticky", top: 0 }}>
            <GoogleMap
              nodes={nodes}
              mapCenter={mapCenter}
              zoom={zoom}
              selected={selectedShop}
              onSelect={setSelectedShop}
              onCameraChange={({ center, zoom }) => {
                setMapCenter(center);
                setZoom(zoom);
              }}
              userLocation={userLocation}
              hoursToday={hoursToday}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default GoogleWrapper;
